/**
 * Профайлер для ResourceKernelLoader
 */

import { CopyOutlined, DatabaseOutlined, EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Input, Modal, Select, Space, Table, Tag, Typography, message } from 'antd'
import React, { useMemo, useState } from 'react'
import { Kernel } from '../../../Kernel'
import { resourceManager } from '../../../Managers/Resource'
import { DebugProfilerData, DebugProfilerInterface } from '../types'
import { JsonViewer } from '../components/JsonViewer'

const { Text } = Typography

// Компонент для відображення JSON ресурсу в модальному вікні
const ResourceJsonModal: React.FC<{ resource: any; resourceName: string }> = ({ resource, resourceName }) => {
    const [isVisible, setIsVisible] = useState(false)

    const jsonStr = JSON.stringify(resource, null, 2)

    const copyToClipboard = () => {
        navigator.clipboard
            .writeText(jsonStr)
            .then(() => {
                message.success('JSON скопійовано!')
            })
            .catch((err) => {
                console.error('Помилка копіювання:', err)
            })
    }

    return (
        <>
            <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => setIsVisible(true)}
                style={{ padding: 0, height: 'auto' }}
            >
                Деталі
            </Button>
            <Modal
                title={`Ресурс: ${resourceName}`}
                open={isVisible}
                onCancel={() => setIsVisible(false)}
                footer={[
                    <Button key="copy" onClick={copyToClipboard}>
                        Копіювати JSON
                    </Button>,
                    <Button key="close" type="primary" onClick={() => setIsVisible(false)}>
                        Закрити
                    </Button>,
                ]}
                width={900}
                styles={{ body: { padding: '16px' } }}
            >
                <JsonViewer data={resource} showSearch={true} />
            </Modal>
        </>
    )
}

// Компонент для відображення таблиці ресурсів з фільтрами
const ResourcesTable: React.FC<{ resources: any; generatedRoutes: any[]; onRefresh?: () => void }> = ({
    resources,
    generatedRoutes,
    onRefresh,
}) => {
    const [searchText, setSearchText] = useState('')
    const [sourceFilter, setSourceFilter] = useState<string | undefined>(undefined)
    const [hasRoutesFilter, setHasRoutesFilter] = useState<string | undefined>(undefined)

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            message.success('Скопійовано!')
        })
    }

    // Перетворюємо ресурси в масив для відображення
    const resourcesArray = useMemo(() => {
        return Object.entries(resources).map(([key, resource]: [string, any]) => {
            const relatedRoutes = generatedRoutes.filter((route: any) => route.resourceName === key)
            const source = key.includes('.') ? 'container' : 'ship'

            // Конвертуємо restUrl в рядок, якщо це об'єкт
            let restUrlDisplay = '-'
            if (typeof resource.restUrl === 'string') {
                restUrlDisplay = resource.restUrl
            } else if (typeof resource.restUrl === 'object' && resource.restUrl !== null) {
                // Якщо це об'єкт, беремо getCollection або перший доступний endpoint
                restUrlDisplay = resource.restUrl.getCollection || resource.restUrl.get || JSON.stringify(resource.restUrl)
            }

            return {
                key,
                name: key,
                title: resource.title || key,
                pageUrl: resource.pageUrl || '-',
                restUrl: restUrlDisplay,
                fieldsCount: resource.fields ? Object.keys(resource.fields).length : 0,
                hasRoutes: relatedRoutes.length > 0,
                routesCount: relatedRoutes.length,
                source,
                routeConfig: resource.routeConfig || {},
                detailConfig: resource.detailConfig || {},
            }
        })
    }, [resources, generatedRoutes])

    // Отримуємо унікальні значення для фільтрів
    const sources = useMemo(() => {
        const uniqueSources = [...new Set(resourcesArray.map((resource) => resource.source))]
        return uniqueSources.sort()
    }, [resourcesArray])

    // Фільтрація даних
    const filteredResources = useMemo(() => {
        return resourcesArray.filter((resource) => {
            // Пошук за підрядком в імені, заголовку та URL
            const searchMatch =
                searchText === '' ||
                resource.name.toLowerCase().includes(searchText.toLowerCase()) ||
                resource.title.toLowerCase().includes(searchText.toLowerCase()) ||
                resource.pageUrl.toLowerCase().includes(searchText.toLowerCase()) ||
                resource.restUrl.toLowerCase().includes(searchText.toLowerCase())

            // Фільтр за джерелом
            const sourceMatch = sourceFilter === undefined || resource.source === sourceFilter

            // Фільтр за наявністю роутів
            const hasRoutesMatch =
                hasRoutesFilter === undefined ||
                (hasRoutesFilter === 'yes' && resource.hasRoutes) ||
                (hasRoutesFilter === 'no' && !resource.hasRoutes)

            return searchMatch && sourceMatch && hasRoutesMatch
        })
    }, [resourcesArray, searchText, sourceFilter, hasRoutesFilter])

    const columns = [
        {
            title: 'Ключ',
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => (
                <Space size={4} style={{ whiteSpace: 'nowrap' }}>
                    <Text code style={{ fontSize: '13px' }}>
                        {name}
                    </Text>
                    <CopyOutlined
                        style={{ fontSize: '14px', cursor: 'pointer', color: '#1890ff' }}
                        onClick={() => handleCopy(name)}
                    />
                </Space>
            ),
        },
        {
            title: 'Заголовок',
            dataIndex: 'title',
            key: 'title',
            render: (title: string) => <Text style={{ fontSize: '13px' }}>{title}</Text>,
        },
        {
            title: 'Page URL',
            dataIndex: 'pageUrl',
            key: 'pageUrl',
            render: (pageUrl: string) => (
                <Space size={4} style={{ whiteSpace: 'nowrap' }}>
                    <Text code style={{ fontSize: '13px' }}>
                        {pageUrl}
                    </Text>
                    <CopyOutlined
                        style={{ fontSize: '14px', cursor: 'pointer', color: '#1890ff' }}
                        onClick={() => handleCopy(pageUrl)}
                    />
                </Space>
            ),
        },
        {
            title: 'REST URI',
            dataIndex: 'restUrl',
            key: 'restUrl',
            render: (restUrl: string) => (
                <Space size={4} style={{ whiteSpace: 'nowrap' }}>
                    <Text code style={{ fontSize: '13px' }}>
                        {restUrl}
                    </Text>
                    <CopyOutlined
                        style={{ fontSize: '14px', cursor: 'pointer', color: '#1890ff' }}
                        onClick={() => handleCopy(restUrl)}
                    />
                </Space>
            ),
        },
        {
            title: 'Поля',
            dataIndex: 'fieldsCount',
            key: 'fieldsCount',
            render: (count: number) => <Text style={{ fontSize: '13px' }}>{count}</Text>,
        },
        {
            title: 'Роути',
            dataIndex: 'routesCount',
            key: 'routesCount',
            render: (count: number) => (
                <Tag color={count > 0 ? 'green' : 'default'} style={{ fontSize: '12px' }}>
                    {count}
                </Tag>
            ),
        },
        {
            title: 'Джерело',
            dataIndex: 'source',
            key: 'source',
            render: (source: string) => (
                <Tag color={source === 'ship' ? 'blue' : 'orange'} style={{ fontSize: '12px' }}>
                    {source}
                </Tag>
            ),
        },
        {
            title: 'Конфіг роутів',
            dataIndex: 'routeConfig',
            key: 'routeConfig',
            render: (config: any) => {
                const enabledRoutes =
                    Object.entries(config || {})
                        .filter(([, enabled]) => enabled !== false)
                        .map(([key]) => key)
                        .join(', ') || 'default'
                return (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {enabledRoutes}
                    </Text>
                )
            },
        },
        {
            title: 'Деталі',
            key: 'details',
            render: (_: any, record: any) => {
                // Отримуємо повний об'єкт ресурсу з початкових даних
                const fullResource = resources[record.name]

                return fullResource ? (
                    <ResourceJsonModal resource={fullResource} resourceName={record.name} />
                ) : (
                    <Text type="secondary">-</Text>
                )
            },
        },
    ]

    return (
        <div>
            <Space style={{ marginBottom: 16, width: '100%' }} wrap>
                <Input
                    placeholder="Пошук за іменем, заголовком, URL..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 280 }}
                />
                <Select
                    placeholder="Джерело"
                    allowClear
                    value={sourceFilter}
                    onChange={setSourceFilter}
                    style={{ width: 120 }}
                >
                    {sources.map((source) => (
                        <Select.Option key={source} value={source}>
                            {source}
                        </Select.Option>
                    ))}
                </Select>
                <Select
                    placeholder="Роути"
                    allowClear
                    value={hasRoutesFilter}
                    onChange={setHasRoutesFilter}
                    style={{ width: 120 }}
                >
                    <Select.Option value="yes">Є роути</Select.Option>
                    <Select.Option value="no">Немає роутів</Select.Option>
                </Select>
                {onRefresh && (
                    <Button icon={<ReloadOutlined />} onClick={onRefresh}>
                        Оновити
                    </Button>
                )}
            </Space>

            <Table
                columns={columns}
                dataSource={filteredResources}
                pagination={{
                    pageSize: 15,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `Всього ${total} записів`,
                }}
                size="small"
                scroll={{ x: 'max-content' }}
            />
        </div>
    )
}

export class ResourceLoaderProfiler implements DebugProfilerInterface {
    key = 'resource-loader'
    name = 'Ресурси'
    icon = (<DatabaseOutlined />)
    priority = 90

    isEnabled(): boolean {
        return true
    }

    async getData(onRefresh?: () => void): Promise<DebugProfilerData> {
        try {
            const kernel = Kernel.getInstance()
            const resourceLoader = kernel.getLoader('resource') as any

            // Отримуємо ресурси з ResourceManager
            const resources = resourceManager.getAll()

            if (!resourceLoader) {
                // Якщо ResourceLoader не знайдено, але є ресурси в менеджері
                if (Object.keys(resources).length > 0) {
                    const resourcesArray = Object.entries(resources)

                    return {
                        summary: {
                            count: resourcesArray.length,
                            status: 'success',
                            label: `${resourcesArray.length} ресурсів`,
                        },
                        details: {
                            custom: <ResourcesTable resources={resources} generatedRoutes={[]} />,
                        },
                    }
                }

                return {
                    summary: {
                        count: 0,
                        status: 'warning',
                        label: 'Немає даних',
                    },
                    details: {
                        stats: [
                            {
                                title: 'Статус',
                                value: 'ResourceLoader не знайдено',
                                color: '#faad14',
                            },
                        ],
                    },
                }
            }

            const generatedRoutes = resourceLoader.getGeneratedRoutes ? resourceLoader.getGeneratedRoutes() : []

            if (Object.keys(resources).length === 0) {
                return {
                    summary: {
                        count: 0,
                        status: 'warning',
                        label: 'Немає ресурсів',
                    },
                    details: {
                        stats: [
                            {
                                title: 'Статус',
                                value: 'Ресурси не завантажені',
                                color: '#faad14',
                            },
                        ],
                    },
                }
            }

            const resourcesArray = Object.entries(resources)

            return {
                summary: {
                    count: resourcesArray.length,
                    status: resourcesArray.length > 0 ? 'success' : 'warning',
                    label: `${resourcesArray.length} ресурсів`,
                },
                details: {
                    custom: onRefresh ? (
                        <ResourcesTable
                            resources={resources}
                            generatedRoutes={generatedRoutes}
                            onRefresh={onRefresh}
                        />
                    ) : (
                        <ResourcesTable resources={resources} generatedRoutes={generatedRoutes} />
                    ),
                },
            }
        } catch (error) {
            console.error('Помилка отримання даних ресурсів:', error)

            return {
                summary: {
                    count: 0,
                    status: 'error',
                    label: 'Помилка',
                },
                details: {
                    stats: [
                        {
                            title: 'Помилка',
                            value: error instanceof Error ? error.message : 'Невідома помилка',
                            color: '#ff4d4f',
                        },
                    ],
                },
            }
        }
    }
}
